import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";

const EUR_TO_KMF = 491.96;
const toKMF = (eur) => Math.round(parseFloat(eur) * EUR_TO_KMF).toLocaleString("fr-FR");
const toEUR = (eur) => parseFloat(eur).toFixed(2);

export default function MerchantPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(0);
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [payHistory, setPayHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [holderName, setHolderName] = useState("");
  const [holderPhone, setHolderPhone] = useState("");
  const [holderEmail, setHolderEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [saleResult, setSaleResult] = useState(null);
  const [mySales, setMySales] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => { loadEvents(); }, []);
  useEffect(() => { if (tab === 1) loadMySales(); if (tab === 2) loadDashboard(); }, [tab]);

  const loadDashboard = async () => {
    setDashLoading(true);
    try {
      const [dashRes, histRes] = await Promise.all([
        api.get('/merchant/dashboard'),
        api.get('/merchant/commission-history')
      ]);
      setDashboard(dashRes.data);
      setPayHistory(histRes.data);
    } catch (e) { console.error(e); }
    finally { setDashLoading(false); }
  };

  const loadEvents = async () => {
    try { const res = await api.get("/events"); setEvents(res.data); }
    catch { setError("Erreur chargement des &eacute;v&eacute;nements"); }
  };

  const loadMySales = async () => {
    try { const res = await api.get("/tickets/my-sales"); setMySales(res.data); }
    catch { setError("Erreur chargement des ventes"); }
  };

  const handleSelectEvent = async (evt) => {
    setSelectedCategory(null); setQuantity(1);
    try {
      const res = await api.get("/events/" + evt.id);
      setSelectedEvent(res.data);
      if (res.data.categories && res.data.categories.length > 0) setSelectedCategory(res.data.categories[0]);
    } catch { setSelectedEvent(evt); }
  };

  const handleSell = async () => {
    if (!selectedEvent) return setError("S&eacute;lectionnez un &eacute;v&eacute;nement");
    if (!selectedCategory) return setError("S&eacute;lectionnez une cat&eacute;gorie");
    setError(""); setLoading(true);
    try {
      const res = await api.post("/tickets/sell", {
        event_id: selectedEvent.id, category_id: selectedCategory.id, quantity,
        holder_name: holderName || undefined,
        holder_phone: holderPhone || undefined,
        holder_email: holderEmail || undefined,
      });
      setSaleResult(res.data);
    } catch (err) {
      setError(err.response && err.response.data ? err.response.data.error : "Erreur lors de la vente");
    } finally { setLoading(false); }
  };

  const resetSale = () => {
    setSaleResult(null); setSelectedEvent(null); setSelectedCategory(null);
    setQuantity(1); setHolderName(""); setHolderPhone(""); setHolderEmail(""); setError("");
  };

  const handlePrint = (ticket, saleData) => {
    const w = window.open("", "_blank");
    w.document.write("<html><head><title>Billet</title><style>body{font-family:Arial,sans-serif;padding:20px;max-width:400px;margin:0 auto;}.hdr{background:#0f1629;color:white;padding:20px;text-align:center;border-radius:12px 12px 0 0;}.logo{font-size:18px;font-weight:800;}.bdy{border:2px solid #0f1629;border-top:none;border-radius:0 0 12px 12px;padding:20px;}.name{font-size:16px;font-weight:700;color:#0f1629;margin-bottom:10px;}.info{font-size:12px;color:#475569;margin-bottom:5px;}.qr{text-align:center;margin:14px 0;}.qr img{width:160px;height:160px;}.pkmf{font-size:20px;font-weight:800;color:#0f1629;text-align:center;}.peur{font-size:11px;color:#94a3b8;text-align:center;}.ref{text-align:center;font-size:10px;color:#94a3b8;margin-top:6px;font-family:monospace;}.ftr{background:#f8fafc;padding:10px;text-align:center;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;margin-top:10px;border-radius:8px;}</style></head><body>");
    w.document.write("<div class=hdr><div class=logo>ComoTicket</div></div>");
    w.document.write("<div class=bdy>");
    w.document.write("<div class=name>" + saleData.event_name + "</div>");
    w.document.write("<div class=info>" + saleData.location + "</div>");
    if (saleData.holder_name) w.document.write("<div class=info>" + saleData.holder_name + "</div>");
    w.document.write("<div class=qr><img src=" + ticket.qr_base64 + " /></div>");
    w.document.write("<div class=pkmf>" + toKMF(saleData.price_per_ticket) + " KMF</div>");
    w.document.write("<div class=peur>" + toEUR(saleData.price_per_ticket) + " EUR</div>");
    w.document.write("<div class=ref>#" + saleData.sale_reference + "</div>");
    w.document.write("</div><div class=ftr>Montrez ce QR code a l entree - Billet non remboursable</div>");
    w.document.write("<scr"+"ipt>window.onload=function(){window.print();}</"+"script></body></html>");
    w.document.close();
  };

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="logo-mark">
            <div className="logo-icon">&#127915;</div>
            <div><div className="logo-text">ComoTicket</div><div className="logo-sub">Billetterie</div></div>
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-label">Menu</div>
            <button className={"nav-item " + (tab === 0 ? "active" : "")} onClick={() => { setTab(0); setSaleResult(null); }}>
              <span className="nav-icon">&#128176;</span>Vendre un billet
            </button>
            <button className={"nav-item " + (tab === 1 ? "active" : "")} onClick={() => setTab(1)}>
              <span className="nav-icon">&#128203;</span>Mes ventes
            </button>
            <button className={"nav-item " + (tab === 2 ? "active" : "")} onClick={() => setTab(2)}>
              <span className="nav-icon">&#128184;</span>Mes commissions
            </button>
          </div>
        </nav>
        <div style={{padding:"8px 12px"}}>
          <a href="/reports" style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:8,background:"#eff6ff",color:"#2563eb",textDecoration:"none",fontWeight:700,fontSize:13}}>
            <span>&#128202;</span> Mes rapports
          </a>
        </div>
        <div className="sidebar-footer">
          <div className="user-card">
            <div className="avatar">{user && user.name ? user.name[0].toUpperCase() : "U"}</div>
            <div className="user-info">
              <div className="user-name">{user && user.name}</div>
              <div className="user-role">Commer&ccedil;ant</div>
            </div>
            <button className="logout-btn" onClick={logout}>&#8618;</button>
          </div>
        </div>
      </aside>
      <main className="main-content">
        <div className="page-header">
          <div className="page-title">Tableau de bord</div>
          <div className="page-subtitle">Bienvenue, {user && user.name}</div>
        </div>
        <div className="page-body">
          {error && <div className="error-msg" style={{marginBottom:16}}><span dangerouslySetInnerHTML={{__html: "&#9888; " + error}} /></div>}
          {tab === 0 && !saleResult && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 400px",gap:24}}>
              <div className="card">
                <div className="card-header"><div className="card-title">S&eacute;lectionner un &eacute;v&eacute;nement</div></div>
                <div className="events-grid">
                  {events.length === 0 && <div style={{color:"var(--gray-400)",fontSize:14}}>Aucun &eacute;v&eacute;nement disponible</div>}
                  {events.map((evt) => {
                    const available = parseInt(evt.available_tickets) || 0;
                    const total = parseInt(evt.total_tickets) || 1;
                    const sold = parseInt(evt.sold_tickets) || 0;
                    const pct = Math.round((sold / total) * 100);
                    return (
                      <div key={evt.id} className={"event-card " + (selectedEvent && selectedEvent.id === evt.id ? "selected" : "")} onClick={() => handleSelectEvent(evt)}>
                        {evt.image_url && <img src={evt.image_url} alt={evt.name} style={{width:"100%",height:120,objectFit:"cover",borderRadius:8,marginBottom:12}} />}
                        <div className="event-name">{evt.name}</div>
                        <div className="event-meta">&#128197; {new Date(evt.date).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric",timeZone:"UTC"})}</div>
                        <div className="event-meta">&#128205; {evt.location}</div>
                        <div style={{marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span className={"badge " + (available > 0 ? "badge-green" : "badge-red")}>{available} dispo</span>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontWeight:800,color:"var(--blue)",fontSize:14}}>{toKMF(evt.ticket_price)} KMF</div>
                            <div style={{fontSize:10,color:"var(--gray-400)"}}>{toEUR(evt.ticket_price)} EUR</div>
                          </div>
                        </div>
                        <div className="stock-bar"><div className="stock-fill" style={{width:pct+"%"}} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="card" style={{position:"sticky",top:24}}>
                  <div className="card-header"><div className="card-title">Vente de billet</div></div>
                  {!selectedEvent ? (
                    <div style={{textAlign:"center",padding:"32px 0",color:"var(--gray-400)"}}>
                      <div style={{fontSize:40,marginBottom:12}}>&#128072;</div>
                      <div>S&eacute;lectionnez un &eacute;v&eacute;nement</div>
                    </div>
                  ) : (
                    <>
                      {selectedEvent.image_url && <img src={selectedEvent.image_url} alt={selectedEvent.name} style={{width:"100%",height:130,objectFit:"cover",borderRadius:8,marginBottom:14}} />}
                      <div style={{background:"var(--gray-50)",borderRadius:8,padding:12,marginBottom:14}}>
                        <div style={{fontWeight:700,color:"var(--navy)",marginBottom:2}}>{selectedEvent.name}</div>
                        <div style={{fontSize:13,color:"var(--gray-600)"}}>&#128205; {selectedEvent.location}</div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Cat&eacute;gorie</label>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {selectedEvent.categories && selectedEvent.categories.length > 0 ? selectedEvent.categories.map((cat) => (
                            <div key={cat.id} onClick={() => setSelectedCategory(cat)}
                              style={{border:selectedCategory && selectedCategory.id===cat.id?"2px solid var(--blue)":"1.5px solid var(--gray-200)",borderRadius:8,padding:"8px 12px",cursor:"pointer",background:selectedCategory && selectedCategory.id===cat.id?"#eff6ff":"white",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <div>
                                <div style={{fontWeight:600,fontSize:13}}>{cat.name}</div>
                                <div style={{fontSize:11,color:"var(--gray-400)"}}>{cat.available_tickets} places</div>
                              </div>
                              <div style={{textAlign:"right"}}>
                                <div style={{fontWeight:800,color:"var(--blue)",fontSize:14}}>{toKMF(cat.price)} KMF</div>
                                <div style={{fontSize:10,color:"var(--gray-400)"}}>{toEUR(cat.price)} EUR</div>
                              </div>
                            </div>
                          )) : <div style={{fontSize:13,color:"var(--gray-400)"}}>Chargement...</div>}
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Quantit&eacute;</label>
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <button className="btn btn-ghost" onClick={() => setQuantity(Math.max(1,quantity-1))}>-</button>
                          <span style={{fontSize:20,fontWeight:700,minWidth:40,textAlign:"center"}}>{quantity}</span>
                          <button className="btn btn-ghost" onClick={() => setQuantity(Math.min(10,quantity+1))}>+</button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Nom du porteur</label>
                        <input className="form-input" placeholder="Mohamed Ali" value={holderName} onChange={e => setHolderName(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">T&eacute;l&eacute;phone WhatsApp</label>
                        <input className="form-input" placeholder="+269 321 0000" value={holderPhone} onChange={e => setHolderPhone(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email (billet envoy&eacute; automatiquement)</label>
                        <input type="email" className="form-input" placeholder="client@email.com" value={holderEmail} onChange={e => setHolderEmail(e.target.value)} />
                      </div>
                      {selectedCategory && (
                        <div style={{borderTop:"1px solid var(--gray-200)",paddingTop:12,marginTop:8}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:13}}>
                            <span style={{color:"var(--gray-600)"}}>{selectedCategory.name} x {quantity}</span>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontWeight:700}}>{toKMF(parseFloat(selectedCategory.price)*quantity)} KMF</div>
                              <div style={{fontSize:10,color:"var(--gray-400)"}}>{(parseFloat(selectedCategory.price)*quantity).toFixed(2)} EUR</div>
                            </div>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:13}}>
                            <span style={{color:"var(--gray-600)"}}>Total &agrave; encaisser</span>
                            <div style={{color:"var(--navy)",fontWeight:700}}>{toKMF(parseFloat(selectedCategory.price)*quantity)} KMF</div>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:13}}>
                            <span style={{color:"var(--gray-600)"}}>Votre commission ({selectedEvent.commission_rate}%)</span>
                            <div style={{color:"var(--green)",fontWeight:600}}>+{toKMF(parseFloat(selectedCategory.price)*quantity*(parseFloat(selectedEvent.commission_rate)/100))} KMF</div>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:800,color:"var(--red)",paddingTop:8,borderTop:"1px dashed var(--gray-200)",marginTop:4}}>
                            <span>&Agrave; reverser &agrave; l'organisateur</span>
                            <div style={{textAlign:"right"}}>
                              <div>{toKMF(parseFloat(selectedCategory.price)*quantity*(1-(parseFloat(selectedEvent.commission_rate)/100)))} KMF</div>
                              <div style={{fontSize:10,color:"var(--gray-400)",fontWeight:400}}>{(parseFloat(selectedCategory.price)*quantity*(1-(parseFloat(selectedEvent.commission_rate)/100))).toFixed(2)} EUR</div>
                            </div>
                          </div>
                        </div>
                      )}
                      <button className="btn btn-primary btn-lg btn-full" style={{marginTop:14}} onClick={handleSell} disabled={loading||!selectedCategory}>
                        {loading ? "Traitement..." : "Valider la vente"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          {tab === 0 && saleResult && (
            <div style={{maxWidth:560,margin:"0 auto"}}>
              <div className="card">
                <div style={{textAlign:"center",marginBottom:20}}>
                  <div style={{width:60,height:60,background:"var(--green)",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:26,color:"white"}}>&#10003;</div>
                  <div className="sale-success">Vente r&eacute;ussie !</div>
                  {saleResult.email_sent && <div style={{fontSize:13,color:"var(--green)",marginTop:4}}>&#128231; Billet envoy&eacute; par email</div>}
                </div>
                <div style={{background:"var(--gray-50)",borderRadius:10,padding:16,marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:15,color:"var(--navy)",marginBottom:10}}>{saleResult.event_name}</div>
                  <div className="ticket-details">
                    <div>&#128205; <strong>{saleResult.location}</strong></div>
                    <div>&#127915; <strong>{saleResult.category}</strong> x {saleResult.quantity}</div>
                    <div>&#128176; <strong>{toKMF(saleResult.total_paid)} KMF</strong> <span style={{fontSize:11,color:"var(--gray-400)"}}> ({saleResult.total_paid} EUR)</span></div>
                    {saleResult.holder_name && <div>&#128100; <strong>{saleResult.holder_name}</strong></div>}
                  </div>
                </div>
                <div className="sale-ref" style={{marginBottom:14}}>ID : #{saleResult.sale_reference}</div>
                {saleResult.tickets && saleResult.tickets.map((t, i) => (
                  <div key={i} style={{border:"1px solid var(--gray-200)",borderRadius:10,padding:14,marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                      <img src={t.qr_base64} alt="QR" style={{width:85,height:85,borderRadius:8}} />
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,color:"var(--gray-600)",marginBottom:3}}>Billet {i+1}/{saleResult.quantity}</div>
                        <div style={{fontFamily:"monospace",fontSize:9,color:"var(--gray-400)",wordBreak:"break-all"}}>{t.qr_code}</div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn btn-ghost" style={{flex:1,fontSize:12}} onClick={() => handlePrint(t, saleResult)}>
                        &#128438; Imprimer
                      </button>
                      {saleResult.whatsapp_link && (
                        <a href={saleResult.whatsapp_link} target="_blank" rel="noreferrer"
                          style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:"#25d366",color:"white",borderRadius:8,padding:"10px",fontSize:12,fontWeight:600,textDecoration:"none"}}>
                          &#128222; WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                <button className="btn btn-ghost btn-full" style={{marginTop:8}} onClick={resetSale}>Nouvelle vente</button>
              </div>
            </div>
          )}
          {tab === 2 && (
            <div className="card">
              <div className="card-header"><div className="card-title">Mes commissions</div></div>
              {dashLoading ? (
                <div style={{padding:32,textAlign:"center",color:"var(--gray-400)"}}>Chargement...</div>
              ) : dashboard ? (
                <>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:24}}>
                    <div style={{background:"var(--gray-50)",borderRadius:12,padding:16,textAlign:"center"}}>
                      <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:4}}>Chiffre d'affaires total</div>
                      <div style={{fontSize:22,fontWeight:800,color:"var(--navy)"}}>{Math.round(parseFloat(dashboard.global.total_revenue) * 491.96).toLocaleString("fr-FR")} KMF</div>
                    </div>
                    <div style={{background:"#f0fdf4",borderRadius:12,padding:16,textAlign:"center"}}>
                      <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:4}}>Commissions dues</div>
                      <div style={{fontSize:22,fontWeight:800,color:"var(--green)"}}>{Math.round(parseFloat(dashboard.global.commission_due) * 491.96).toLocaleString("fr-FR")} KMF</div>
                    </div>
                    <div style={{background:"#eff6ff",borderRadius:12,padding:16,textAlign:"center"}}>
                      <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:4}}>Commissions re&ccedil;ues</div>
                      <div style={{fontSize:22,fontWeight:800,color:"#2563eb"}}>{Math.round(parseFloat(dashboard.global.commission_paid) * 491.96).toLocaleString("fr-FR")} KMF</div>
                    </div>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table>
                      <thead>
                        <tr><th>&Eacute;v&eacute;nement</th><th>Date</th><th>Billets vendus</th><th>CA total</th><th>Taux</th><th>Commission due</th><th>Commission re&ccedil;ue</th></tr>
                      </thead>
                      <tbody>
                        {dashboard.by_event.map((evt, i) => (
                          <tr key={i}>
                            <td style={{fontWeight:600}}>{evt.event_name}</td>
                            <td>{new Date(evt.event_date).toLocaleDateString("fr-FR",{timeZone:"UTC"})}</td>
                            <td style={{textAlign:"center"}}>{evt.sales_count}</td>
                            <td>{Math.round(parseFloat(evt.revenue) * 491.96).toLocaleString("fr-FR")} KMF</td>
                            <td style={{textAlign:"center"}}>{evt.commission_rate}%</td>
                            <td style={{color:"var(--green)",fontWeight:700}}>{Math.round(parseFloat(evt.commission_due) * 491.96).toLocaleString("fr-FR")} KMF</td>
                            <td style={{color:"#2563eb",fontWeight:700}}>{Math.round(parseFloat(evt.commission_paid) * 491.96).toLocaleString("fr-FR")} KMF</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="card" style={{marginTop:20}}>
                    <div className="card-header"><div className="card-title">Historique des paiements re&ccedil;us</div></div>
                    <div style={{overflowX:"auto"}}>
                      <table>
                        <thead>
                          <tr><th>Date</th><th>&Eacute;v&eacute;nement</th><th>Montant re&ccedil;u</th><th>Pay&eacute; par</th><th>Note</th></tr>
                        </thead>
                        <tbody>
                          {payHistory.length === 0 ? (
                            <tr><td colSpan={5} style={{textAlign:"center",padding:24,color:"var(--gray-400)"}}>Aucun paiement re&ccedil;u pour le moment</td></tr>
                          ) : payHistory.map((p) => (
                            <tr key={p.id}>
                              <td style={{fontSize:12}}>
                                <div style={{fontWeight:600}}>{new Date(p.payment_date || p.created_at).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</div>
                                <div style={{fontSize:11,color:"var(--gray-400)"}}>Enreg.: {new Date(p.created_at).toLocaleDateString("fr-FR",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                              </td>
                              <td>{p.event_name || "Toutes ventes"}</td>
                              <td style={{color:"var(--green)",fontWeight:700}}>{Math.round(parseFloat(p.amount)*491.96).toLocaleString("fr-FR")} KMF</td>
                              <td>{p.paid_by_name}</td>
                              <td style={{fontSize:12,color:"var(--gray-500)"}}>{p.note || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{padding:32,textAlign:"center",color:"var(--gray-400)"}}>Aucune donn&eacute;e disponible</div>
              )}
            </div>
          )}

          {tab === 1 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Mes ventes r&eacute;centes</div>
                <button className="btn btn-ghost" onClick={loadMySales}>Actualiser</button>
              </div>
              <div style={{overflowX:"auto"}}>
                <table>
                  <thead>
                    <tr><th>Reference</th><th>&Eacute;v&eacute;nement</th><th>Cat&eacute;gorie</th><th>Prix</th><th>Commission</th><th>Date</th><th>Statut</th></tr>
                  </thead>
                  <tbody>
                    {mySales.length === 0 && <tr><td colSpan={7} style={{textAlign:"center",color:"var(--gray-400)",padding:32}}>Aucune vente</td></tr>}
                    {mySales.map((s) => (
                      <tr key={s.id}>
                        <td><span className="sale-ref" style={{fontSize:12}}>#{s.sale_reference}</span></td>
                        <td>{s.event_name}</td>
                        <td><span className="badge badge-blue">{s.category_name || "Standard"}</span></td>
                        <td><strong>{toKMF(s.price)} KMF</strong><div style={{fontSize:10,color:"var(--gray-400)"}}>{s.price} EUR</div></td>
                        <td style={{color:"var(--red)"}}>-{toKMF(s.commission)} KMF</td>
                        <td style={{fontSize:12,color:"var(--gray-600)"}}>{new Date(s.created_at).toLocaleDateString("fr-FR")}</td>
                        <td><span className={"badge badge-" + (s.ticket_status==="used"?"gray":"green")}>{s.ticket_status==="used"?"Utilise":"Vendu"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}